import React, { useState } from "react";
import { supabase } from "../lib/supabase";
import { Button } from "../components/Button";
import { SectionAccordion } from "../components/SectionAccordion";
import { WeeklyCategoryManager } from "../components/WeeklyCategoryManager";
import { CalendarCheck, CheckCircle2, Loader2, XCircle } from "lucide-react";

export function WeeklyReview() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [addedActions, setAddedActions] = useState<string[]>([]);
  const [openCategorySection, setOpenCategorySection] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      if (!session?.user) throw new Error("Not logged in");

      const since = new Date();
      since.setDate(since.getDate() - 7);
      const isoSince = since.toISOString();

      const { data: growthData } = await supabase
        .from("growth_days")
        .select("date, journaled, task_completed, lesson_learned")
        .eq("user_id", session.user.id)
        .gte("date", isoSince);

      const journaledDays = growthData.filter((d) => d.journaled).length;
      const actionDays = growthData.filter((d) => d.task_completed).length;
      const lessonDays = growthData.filter((d) => d.lesson_learned).length;

      const decryptedRes = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/decrypt-user-entries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ type: "lessons" })
      });

      if (!decryptedRes.ok) throw new Error("Failed to load decrypted lessons");
      const { textContent } = await decryptedRes.json();

      const decryptedLessons = textContent
        .split("----------------------------------------")
        .map((t) => t.trim())
        .filter(Boolean)
        .map((text) => ({
          type: "lesson",
          created_at: new Date().toISOString(),
          content: text
        }));

      const reviewRes = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/review-categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          lessons: decryptedLessons,
          growth: { journaledDays, actionDays, lessonDays }
        })
      });

      if (!reviewRes.ok) throw new Error("Failed to process reflection");
      const result = await reviewRes.json();
      setSummary(result.summary);
      setCategories(result.categories);
    } catch (err) {
      console.error("Weekly Review Error:", err);
      setError("Failed to load weekly review.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAction = async (text: string) => {
    const {
      data: { session }
    } = await supabase.auth.getSession();
    if (!session?.user) return;

    const isAdded = addedActions.includes(text);
    if (isAdded) {
      const { data: matches } = await supabase
        .from("tasks")
        .select("id")
        .eq("user_id", session.user.id)
        .eq("text", text);
      if (matches?.length) {
        await supabase.from("tasks").delete().eq("id", matches[0].id);
      }
      setAddedActions((prev) => prev.filter((t) => t !== text));
    } else {
      await supabase.from("tasks").insert({
        user_id: session.user.id,
        text,
        completed: false
      });
      setAddedActions((prev) => [...prev, text]);
    }
  };

  return (
    <div className="p-4 space-y-6">
      <h2 className="text-xl font-bold">Weekly Reflection</h2>

       <SectionAccordion
  title="Insights & Actions by Category"
  icon={<CalendarCheck />}  // ✅ JSX element
  open={openCategorySection}
  toggle={() => setOpenCategorySection(prev => !prev)}
  sectionKey="weeklyCategoryInsights"
>
  <WeeklyCategoryManager />
</SectionAccordion>

      <Button onClick={fetchData} disabled={loading}>
        {loading ? <Loader2 className="animate-spin mr-2 inline-block" /> : null}
        {loading ? "Running..." : "Run Weekly Review"}
      </Button>

      {error && (
        <div className="text-red-600 p-4">
          <XCircle className="inline-block mr-2" /> {error}
        </div>
      )}

      {summary && (
        <>
          <p className="text-gray-700 whitespace-pre-line">{summary?.text}</p>
          <ul className="list-disc ml-6">
            {summary?.focus_points?.map((fp, i) => (
              <li key={i} className="text-green-700 font-medium">{fp}</li>
            ))}
          </ul>
        </>
      )}

    

    </div>
  );
}
